import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { CabinetLayout, PROFILE_TABS, type CabinetNav, type ProfileTab } from '../components/CabinetLayout';
import { BannerLivePreview } from '../components/BannerLivePreview';
import {
  IconAndroid,
  IconApple,
  IconAlert,
  IconCalendar,
  IconCheck,
  IconChevron,
  IconCreditCard,
  IconCrown,
  IconDevice,
  IconDoc,
  IconDownload,
  IconBook,
  IconGlobe,
  IconHelp,
  IconInfo,
  IconKey,
  IconLinux,
  IconLock,
  IconPlus,
  IconRefresh,
  IconRouter,
  IconSearch,
  IconShield,
  IconStar,
  IconTrash,
  IconWindows,
  IconX,
} from '../components/icons';
import { HelpCenter, prepareHelpScreen } from '../components/HelpCenter';
import { RegionDiagnosticsPanel } from '../components/RegionDiagnosticsPanel';
import { RegionFlag } from '../components/RegionFlag';
import { CreateKeyModal, type CreateKeyProtocol } from '../components/CreateKeyModal';
import { SupportNotifyBridge } from '../components/SupportNotifyBridge';
import { SupportSection } from '../components/SupportSection';
import { SplashScreen } from '../components/SplashScreen';
import { useAuth } from '../hooks/useAuth';
import { usePreferences } from '../hooks/usePreferences';
import { useAnimatedModal } from '../hooks/useAnimatedModal';
import {
  api,
  ensurePortalSession,
  clearPendingPaymentId,
  getPendingPaymentId,
  portalApiUrl,
  setPendingPaymentId,
  type PortalInviteInfo,
  type PortalPromoHistory,
  type PortalOvpnKeyItem,
  type PortalPaymentItem,
  type PortalProfile,
  type PortalRegionItem,
  type PortalSubscriptionOffers,
  type PortalTierChangePreview,
  type PortalVpnKeyConfig,
  type PortalVpnKeyItem,
} from '../services/api';
import {
  formatDate,
  formatDateLong,
  formatDateShort,
  formatMoney,
  isAndroidDevice,
  isIosLikeDevice,
  navigateDownloadUrl,
  paymentHeadline,
  paymentStatusBadgeClass,
  paymentStatusIconIsError,
  paymentStatusLabel,
  periodDiscountPercent,
  daysUntil,
  pluralDevices,
  pluralProfiles,
  saveConfigFile,
  subStatusLabel,
  subscriptionDaysLeftLabel,
  subscriptionProgress,
  subscriptionUntilCopy,
  tierLabel,
  pluralDays,
} from '../utils/format';
import { parseWgInterfaceAddress, vpnAppLinks } from '../utils/vpnApps';
import { confirmAction } from '../utils/confirm';
import {
  bannerUrlForCode,
  clearPendingInvite,
  peekPendingInvite,
} from '../utils/inviteLink';
import {
  DEFAULT_SIMPLE_BANNER_TEXT,
  BANNER_FROM_OPTIONS,
  BANNER_LAYOUT_OPTIONS,
  BANNER_THEME_PRESETS,
  DEFAULT_BANNER_OPTIONS,
  normalizeBannerOptions,
  readBannerOptions,
  writeBannerOptions,
  type BannerDisplayOptions,
  type BannerFrom,
  type BannerLayout,
  type BannerMode,
} from '../utils/bannerDisplay';
import { PROMO_ICONS, promoIconUrl } from '../utils/promoAssets';
import { onAppResume, openExternalUrl } from '../utils/telegramWebApp';
import { requestOpenSupportCreate, requestOpenSupportTicket } from '../utils/supportOpen';
import {
  clearOnceCredentials,
  peekOnceCredentials,
  type OnceCredentials,
} from '../utils/websiteCredentials';

type VpnProtocol = 'wg' | 'ovpn';

export function CabinetPage() {
  const { user, config: authConfig, logout, refresh, isAdmin } = useAuth();
  const { theme, navPosition, setTheme, setNavPosition } = usePreferences();
  const [nav, setNav] = useState<CabinetNav>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const navParam = params.get('nav');
      if (
        navParam === 'home' ||
        navParam === 'profile' ||
        navParam === 'help' ||
        navParam === 'support' ||
        navParam === 'settings'
      ) {
        return navParam;
      }
      const raw = sessionStorage.getItem('yp_portal_nav');
      if (
        raw === 'home' ||
        raw === 'profile' ||
        raw === 'help' ||
        raw === 'support' ||
        raw === 'settings'
      ) {
        return raw;
      }
    } catch {
      /* ignore */
    }
    return 'home';
  });
  const [profileTab, setProfileTab] = useState<ProfileTab>('subscription');
  const [vpnProtocol, setVpnProtocol] = useState<VpnProtocol>('wg');
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [wgKeys, setWgKeys] = useState<PortalVpnKeyItem[]>([]);
  const [ovpnKeys, setOvpnKeys] = useState<PortalOvpnKeyItem[]>([]);
  const [wgServers, setWgServers] = useState<string[]>([]);
  const [ovpnServers, setOvpnServers] = useState<string[]>([]);
  const [vpnRegions, setVpnRegions] = useState<PortalRegionItem[]>([]);
  const [keysSearch, setKeysSearch] = useState('');
  const [keysRegionFilter, setKeysRegionFilter] = useState('all');
  const [createKeyOpen, setCreateKeyOpen] = useState(false);
  const [createKeyProtocol, setCreateKeyProtocol] = useState<CreateKeyProtocol>('wg');
  const [createKeyRegion, setCreateKeyRegion] = useState('');
  const [offers, setOffers] = useState<PortalSubscriptionOffers | null>(null);
  const [selectedPayTier, setSelectedPayTier] = useState('premium');
  const [selectedPayPeriod, setSelectedPayPeriod] = useState('1');
  const [tierChangePreview, setTierChangePreview] = useState<PortalTierChangePreview | null>(null);
  const paySelectionInitRef = useRef(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState<string | null>(null);
  const [payErr, setPayErr] = useState<string | null>(null);
  const [paymentChecking, setPaymentChecking] = useState(false);
  const [payments, setPayments] = useState<PortalPaymentItem[]>([]);
  const paymentPollAbortRef = useRef(false);
  const paymentPollRunningRef = useRef(false);
  const [invite, setInvite] = useState<PortalInviteInfo | null>(null);
  const [promoHistory, setPromoHistory] = useState<PortalPromoHistory | null>(null);
  const [newReferralCode, setNewReferralCode] = useState('');
  const [referralBusy, setReferralBusy] = useState(false);
  const [referralMsg, setReferralMsg] = useState<string | null>(null);
  const [referralErr, setReferralErr] = useState<string | null>(null);
  const [showReferralEdit, setShowReferralEdit] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [bannerCopied, setBannerCopied] = useState(false);
  const [bannerOpts, setBannerOpts] = useState<BannerDisplayOptions>(() => readBannerOptions());
  const [showBannerSettings, setShowBannerSettings] = useState(false);
  const [bannerSettingsTab, setBannerSettingsTab] = useState<'content' | 'look' | 'motion'>(
    'content',
  );
  const [historyTab, setHistoryTab] = useState<'invitees' | 'activations'>('invitees');
  const [historyVisible, setHistoryVisible] = useState(8);
  const bannerHydratedRef = useRef(false);
  const bannerSaveTimerRef = useRef<number | null>(null);
  const bannerBackdropDownRef = useRef(false);
  const referralBackdropDownRef = useRef(false);
  const downloadBackdropDownRef = useRef(false);
  const [inviteFromLink, setInviteFromLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoMsg, setPromoMsg] = useState<string | null>(null);
  const [promoErr, setPromoErr] = useState<string | null>(null);
  const [keyConfig, setKeyConfig] = useState<PortalVpnKeyConfig | null>(null);
  const [configBusyId, setConfigBusyId] = useState<number | null>(null);
  const [showConfigText, setShowConfigText] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState<number | null>(null);
  const [keyMsg, setKeyMsg] = useState<string | null>(null);
  const [keyErr, setKeyErr] = useState<string | null>(null);
  const [wgBusy, setWgBusy] = useState(false);
  const [ovpnBusy, setOvpnBusy] = useState(false);
  const [selectedWgServer, setSelectedWgServer] = useState('');
  const [selectedOvpnServer, setSelectedOvpnServer] = useState('');
  const [idCopied, setIdCopied] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadPickerOpen, setDownloadPickerOpen] = useState(false);
  const [downloadSelected, setDownloadSelected] = useState<string[]>([]);
  const bannerSettingsUi = useAnimatedModal(showBannerSettings);
  const referralEditUi = useAnimatedModal(showReferralEdit);
  const downloadPickerUi = useAnimatedModal(downloadPickerOpen);
  const [supportFlash, setSupportFlash] = useState<{ msg: string; ticketId: number } | null>(null);
  const supportFlashTimer = useRef<number | null>(null);
  const [onceCreds, setOnceCreds] = useState<OnceCredentials | null>(() => peekOnceCredentials());
  const [siteUsername, setSiteUsername] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [sitePassMsg, setSitePassMsg] = useState<string | null>(null);
  const [sitePassErr, setSitePassErr] = useState<string | null>(null);
  const [sitePassBusy, setSitePassBusy] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  const showSupportFlash = useCallback((msg: string, ticketId: number) => {
    setSupportFlash({ msg, ticketId });
    if (supportFlashTimer.current != null) window.clearTimeout(supportFlashTimer.current);
    supportFlashTimer.current = window.setTimeout(() => setSupportFlash(null), 4500);
  }, []);

  const openSupportTicket = useCallback((ticketId: number) => {
    setNav('support');
    requestOpenSupportTicket(ticketId, true);
  }, []);

  const botUrl = authConfig?.bot_username ? `https://t.me/${authConfig.bot_username}` : undefined;
  const displayName = user?.first_name ?? user?.username ?? 'пользователь';

  const load = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (silent) setRefreshBusy(true);
    else {
      setLoading(true);
      setLoadError(null);
    }

    try {
      const [prof, wg, ovpn, pays, wgSrv, ovpnSrv, subOffers, regionsRes] = await Promise.all([
        api.getProfile(),
        api.listVpnKeys(),
        api.listOvpnKeys(),
        api.listPayments(),
        api.listWgServers(),
        api.listOvpnServers(),
        api.getSubscriptionOffers(),
        api.listVpnRegions(),
      ]);

      if (prof.success) setProfile(prof.data ?? null);
      else setLoadError(prof.error?.message ?? 'Не удалось загрузить профиль');
      if (wg.success) setWgKeys(wg.data ?? []);
      if (ovpn.success) setOvpnKeys(ovpn.data ?? []);
      if (pays.success) setPayments(pays.data ?? []);
      if (regionsRes.success) setVpnRegions(regionsRes.data ?? []);
      if (wgSrv.success && wgSrv.data) {
        setWgServers(wgSrv.data);
        setSelectedWgServer((prev) => prev || wgSrv.data![0] || '');
      }
      if (ovpnSrv.success && ovpnSrv.data) {
        setOvpnServers(ovpnSrv.data);
        setSelectedOvpnServer((prev) => prev || ovpnSrv.data![0] || '');
      }
      if (subOffers.success && subOffers.data) {
        setOffers(subOffers.data);
        if (!paySelectionInitRef.current) {
          paySelectionInitRef.current = true;
          const tiers = subOffers.data.tiers;
          const sub = prof.success ? prof.data?.subscription : null;
          const subTier = (sub?.subscription_tier || '').toLowerCase();
          const hasActiveSub =
            sub?.subscription_status === 'active' || sub?.subscription_status === 'lifetime';
          const preferred =
            hasActiveSub && subTier && tiers.some((t) => t.tier_id === subTier)
              ? subTier
              : tiers.find((t) => t.tier_id === 'premium')?.tier_id ||
                tiers[0]?.tier_id ||
                'premium';
          const tierOffer = tiers.find((t) => t.tier_id === preferred);
          const period = tierOffer?.periods.some((p) => p.period === '1')
            ? '1'
            : tierOffer?.periods[0]?.period || '1';
          setSelectedPayTier(preferred);
          setSelectedPayPeriod(period);
        }
      }
    } finally {
      if (silent) setRefreshBusy(false);
      else setLoading(false);
    }
  }, []);

  const loadInvite = useCallback(async () => {
    setReferralErr(null);
    const [inviteRes, historyRes] = await Promise.all([
      api.getMyInvite(),
      api.getPromoHistory(),
    ]);
    if (inviteRes.data) {
      setInvite(inviteRes.data);
      setNewReferralCode(inviteRes.data.code);
      if (!bannerHydratedRef.current && inviteRes.data.banner_prefs) {
        bannerHydratedRef.current = true;
        const merged = normalizeBannerOptions({
          ...readBannerOptions(),
          ...(inviteRes.data.banner_prefs as Partial<BannerDisplayOptions>),
        });
        setBannerOpts(merged);
        writeBannerOptions(merged);
      } else {
        bannerHydratedRef.current = true;
      }
    } else if (inviteRes.error) {
      setReferralErr(inviteRes.error.message);
    }
    if (historyRes.data) {
      setPromoHistory(historyRes.data);
    }
  }, []);

  const copyReferralCode = async () => {
    if (!invite?.code) return;
    await navigator.clipboard.writeText(invite.code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const copyReferralLink = async () => {
    if (!invite?.share_url) return;
    await navigator.clipboard.writeText(invite.share_url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const resolvedBannerUrl = invite?.code
    ? bannerUrlForCode(
        invite.code,
        authConfig?.user_portal_url,
        bannerOpts,
      )
    : '';

  const persistBannerPrefsToServer = useCallback((opts: BannerDisplayOptions) => {
    if (bannerSaveTimerRef.current != null) {
      window.clearTimeout(bannerSaveTimerRef.current);
    }
    bannerSaveTimerRef.current = window.setTimeout(() => {
      void api.updateBannerPrefs({ ...normalizeBannerOptions(opts) });
    }, 600);
  }, []);

  const updateBannerOpts = (patch: Partial<BannerDisplayOptions>) => {
    setBannerOpts((prev) => {
      // Keep draft text as typed (spaces/newlines). Only finalize when persisting/URL.
      const draftText =
        typeof patch.text === 'string' ? patch.text.slice(0, 360) : prev.text;
      const normalized = normalizeBannerOptions({ ...prev, ...patch, text: draftText });
      const next = { ...normalized, text: draftText };
      const forPersist = normalizeBannerOptions(next);
      writeBannerOptions(forPersist);
      persistBannerPrefsToServer(forPersist);
      return next;
    });
  };

  const resetBannerDesign = () => {
    updateBannerOpts({
      layout: DEFAULT_BANNER_OPTIONS.layout,
      text: '',
      bg: DEFAULT_BANNER_OPTIONS.bg,
      fg: DEFAULT_BANNER_OPTIONS.fg,
      accent: DEFAULT_BANNER_OPTIONS.accent,
      fx: DEFAULT_BANNER_OPTIONS.fx,
    });
  };

  useEffect(() => {
    if (!showBannerSettings && !showReferralEdit && !downloadPickerOpen && !createKeyOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showBannerSettings) setShowBannerSettings(false);
      else if (showReferralEdit) {
        setShowReferralEdit(false);
        if (invite) setNewReferralCode(invite.code);
        setReferralErr(null);
      } else if (createKeyOpen && !wgBusy && !ovpnBusy) setCreateKeyOpen(false);
      else if (downloadPickerOpen && !downloadBusy) setDownloadPickerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    showBannerSettings,
    showReferralEdit,
    downloadPickerOpen,
    downloadBusy,
    createKeyOpen,
    wgBusy,
    ovpnBusy,
    invite,
  ]);

  const copyBannerLink = async () => {
    if (!resolvedBannerUrl) return;
    await navigator.clipboard.writeText(resolvedBannerUrl);
    setBannerCopied(true);
    setTimeout(() => setBannerCopied(false), 2000);
  };

  const updateReferralCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setReferralBusy(true);
    setReferralMsg(null);
    setReferralErr(null);
    try {
      const res = await api.updateReferralCode(newReferralCode.trim());
      if (res.data?.status === 'ok') {
        setReferralMsg('Личный код обновлён.');
        setShowReferralEdit(false);
        await loadInvite();
      } else if (res.data?.status === 'same') {
        setReferralMsg('У вас уже установлен этот код.');
        setShowReferralEdit(false);
      } else if (res.error) {
        setReferralErr(referralErrorLabel(res.error.code, res.error.message));
        if (res.error.code === 'referral_cooldown') {
          await loadInvite();
        }
      }
    } finally {
      setReferralBusy(false);
    }
  };

  useEffect(() => {
    try {
      sessionStorage.setItem('yp_portal_nav', nav);
    } catch {
      /* ignore */
    }
  }, [nav]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const navParam = params.get('nav');
      if (navParam === 'support') setNav('support');
      const ticketId = Number(params.get('ticket') || 0);
      if (ticketId > 0) {
        // Open after SupportSection mounts (event), not via sticky session restore.
        window.setTimeout(() => requestOpenSupportTicket(ticketId, true), 0);
      }
      if (params.get('create') === '1' || params.get('create') === 'true') {
        try {
          sessionStorage.setItem('yp_portal_support_create', '1');
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (nav !== 'settings') return;
    void (async () => {
      const res = await api.getWebsiteAccount();
      if (res.data?.username) setSiteUsername(res.data.username);
    })();
  }, [nav]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return onAppResume(() => {
      void (async () => {
        await ensurePortalSession();
        await load({ silent: true });
        if (getPendingPaymentId()) {
          void pollPendingPayment({ switchToPayments: false });
        }
      })();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resume hook
  }, [load]);

  useEffect(() => {
    if (nav === 'profile' && profileTab === 'vpn') void load({ silent: true });
  }, [nav, profileTab, load]);

  useEffect(() => {
    if (nav === 'profile' && profileTab === 'promo') loadInvite();
  }, [nav, profileTab, loadInvite]);

  useEffect(() => {
    if (!offers || !selectedPayTier || !selectedPayPeriod) {
      setTierChangePreview(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await api.getTierChangePreview(selectedPayTier, selectedPayPeriod);
      if (cancelled) return;
      setTierChangePreview(res.success ? (res.data ?? null) : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [offers, selectedPayTier, selectedPayPeriod]);

  const pollPendingPayment = useCallback(
    async (opts?: { switchToPayments?: boolean }) => {
      const pendingId = getPendingPaymentId();
      if (!pendingId || paymentPollRunningRef.current) return;

      paymentPollAbortRef.current = false;
      paymentPollRunningRef.current = true;
      setPaymentChecking(true);
      if (opts?.switchToPayments !== false) {
        setProfileTab('payments');
        setNav('profile');
      }

      const maxAttempts = 60; // ~5 min at 5s
      let attempts = 0;

      try {
        while (!paymentPollAbortRef.current && attempts < maxAttempts) {
          const res = await api.getPaymentStatus(pendingId);
          if (paymentPollAbortRef.current) return;

          if (res.data?.status === 'processed') {
            clearPendingPaymentId();
            setPayMsg('Оплата прошла успешно. Подписка обновлена.');
            setPayErr(null);
            await refresh();
            await load();
            return;
          }
          if (res.data?.status === 'cancelled' || res.data?.status === 'canceled') {
            // May still succeed at YooKassa after user dismissed — keep checking a bit,
            // but show soft message only after attempts exhaust unless API is sure.
            // Backend now recovers if YK succeeded; treat cancelled as soft until mid-poll.
            if (attempts >= 6) {
              clearPendingPaymentId();
              setPayErr('Оплата отменена. Если деньги списались — нажмите «Проверить оплату» или напишите в поддержку.');
              setPayMsg(null);
              return;
            }
          }
          if (res.data?.status === 'failed') {
            if (attempts >= 6) {
              clearPendingPaymentId();
              setPayErr(
                'Оплата не подтверждена. Если деньги списались — нажмите «Проверить оплату» или напишите в поддержку с кодом платежа.',
              );
              setPayMsg(null);
              return;
            }
          }
          attempts += 1;
          await new Promise((r) => setTimeout(r, 5000));
        }

        if (!paymentPollAbortRef.current) {
          setPayMsg(
            'Платёж ещё обрабатывается. Вернитесь в кабинет после оплаты — статус обновится автоматически. Можно нажать «Проверить оплату».',
          );
        }
      } finally {
        paymentPollRunningRef.current = false;
        if (!paymentPollAbortRef.current) setPaymentChecking(false);
      }
    },
    [load, refresh],
  );

  useEffect(() => {
    if (!getPendingPaymentId()) return;
    void pollPendingPayment({ switchToPayments: true });
    return () => {
      paymentPollAbortRef.current = true;
    };
  }, [pollPendingPayment]);

  const copyUserId = async () => {
    if (!user) return;
    await navigator.clipboard.writeText(String(user.telegram_user_id));
    setIdCopied(true);
    setTimeout(() => setIdCopied(false), 2000);
  };

  const downloadConfigById = async (protocol: 'wg' | 'ovpn', keyId: number) => {
    setKeyMsg(null);
    setKeyErr(null);

    // Android / WebView: real attachment URL is more reliable than blob downloads.
    if (isAndroidDevice() || isIosLikeDevice()) {
      const linkRes =
        protocol === 'ovpn'
          ? await api.createOvpnDownloadLink(keyId)
          : await api.createWgDownloadLink(keyId);
      if (linkRes.data?.url) {
        navigateDownloadUrl(portalApiUrl(linkRes.data.url));
        setKeyMsg(
          isIosLikeDevice()
            ? protocol === 'ovpn'
              ? 'В меню выберите «Сохранить в Файлы» (не OpenVPN). Потом: Файлы → файл → Поделиться → OpenVPN.'
              : 'В меню выберите «Сохранить в Файлы» или WireGuard.'
            : 'Файл должен появиться в загрузках. Затем откройте его в приложении VPN.',
        );
        return;
      }
      // Fall through to blob/share if token mint failed.
    }

    const res =
      protocol === 'ovpn' ? await api.getOvpnConfig(keyId) : await api.getVpnConfig(keyId);
    if (!res.data) {
      setKeyErr(res.error?.message ?? 'Не удалось получить конфиг');
      return;
    }
    const ext = protocol === 'ovpn' ? 'ovpn' : 'conf';
    const result = await saveConfigFile(
      `yourprov-${res.data.server_code}.${ext}`,
      res.data.config_text,
    );
    if (result === 'shared') {
      setKeyMsg(
        protocol === 'ovpn'
          ? 'В меню выберите «Сохранить в Файлы» (не OpenVPN). Потом: Файлы → файл → Поделиться → OpenVPN.'
          : 'В меню выберите «Сохранить в Файлы» или WireGuard.',
      );
    } else if (result === 'aborted') {
      setKeyMsg('Отменено. Нажмите «Скачать» ещё раз.');
    } else if (result === 'copied') {
      setKeyMsg('Конфиг скопирован в буфер обмена.');
    } else if (result === 'failed') {
      setKeyErr(
        isIosLikeDevice()
          ? 'Не удалось открыть меню «Поделиться». Откройте кабинет в Safari и попробуйте снова.'
          : 'Не удалось сохранить файл. Попробуйте ещё раз или скопируйте текст конфига.',
      );
    } else {
      setKeyMsg('Файл скачан. Откройте его в приложении VPN.');
    }
  };

  const loadWgConfig = async (keyId: number) => {
    setConfigBusyId(keyId);
    setKeyConfig(null);
    setShowConfigText(false);
    setKeyMsg(null);
    setKeyErr(null);
    try {
      const res = await api.getVpnConfig(keyId);
      if (res.data) {
        setKeyConfig(res.data);
        const ip = parseWgInterfaceAddress(res.data.config_text);
        if (ip) {
          setWgKeys((prev) =>
            prev.map((k) => (k.id === keyId && !k.ip_address ? { ...k, ip_address: ip } : k)),
          );
        }
      } else if (res.error) {
        setKeyErr(
          res.error.message.includes('Client') || res.error.code === 'config_unavailable'
            ? 'Конфиг недоступен на сервере (клиент не найден). Удалите ключ и создайте новый.'
            : res.error.message,
        );
      }
    } finally {
      setConfigBusyId(null);
    }
  };

  const loadOvpnConfig = async (keyId: number) => {
    setConfigBusyId(keyId);
    setKeyConfig(null);
    setShowConfigText(false);
    setKeyMsg(null);
    setKeyErr(null);
    try {
      const res = await api.getOvpnConfig(keyId);
      if (res.data) setKeyConfig(res.data);
      else if (res.error) {
        setKeyErr(
          res.error.code === 'config_unavailable'
            ? 'Конфиг недоступен на сервере. Удалите ключ и создайте новый.'
            : res.error.message,
        );
      }
    } finally {
      setConfigBusyId(null);
    }
  };

  const downloadCurrentConfig = async () => {
    if (!keyConfig) return;
    const protocol = keyConfig.protocol === 'ovpn' ? 'ovpn' : 'wg';
    await downloadConfigById(protocol, keyConfig.key_id);
  };

  const openDownloadPicker = () => {
    const ids = [
      ...wgKeys.map((k) => `wg:${k.id}`),
      ...ovpnKeys.map((k) => `ovpn:${k.id}`),
    ];
    if (ids.length === 0) return;
    setDownloadSelected(ids);
    setDownloadPickerOpen(true);
  };

  const toggleDownloadItem = (id: string) => {
    setDownloadSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const downloadSelectedConfigs = async () => {
    if (downloadSelected.length === 0) return;
    setDownloadBusy(true);
    setKeyMsg(null);
    setKeyErr(null);
    try {
      if (isIosLikeDevice()) {
        const first = downloadSelected[0];
        const [kind, rawId] = first.split(':');
        const id = Number(rawId);
        if (Number.isFinite(id) && (kind === 'wg' || kind === 'ovpn')) {
          await downloadConfigById(kind, id);
          if (downloadSelected.length > 1) {
            setKeyMsg(
              `Файл отправлен в «Поделиться». Остальные (${downloadSelected.length - 1}) скачайте по одному.`,
            );
          }
        }
        setDownloadPickerOpen(false);
        return;
      }

      for (const token of downloadSelected) {
        const [kind, rawId] = token.split(':');
        const id = Number(rawId);
        if (!Number.isFinite(id)) continue;
        if (kind === 'wg' || kind === 'ovpn') {
          await downloadConfigById(kind, id);
          await new Promise((r) => setTimeout(r, 300));
        }
      }
      setDownloadPickerOpen(false);
    } finally {
      setDownloadBusy(false);
    }
  };

  const createOvpnKey = async (serverCode?: string) => {
    const server = (serverCode || selectedOvpnServer).trim();
    if (!server) return;
    setOvpnBusy(true);
    setKeyMsg(null);
    setKeyErr(null);
    try {
      const res = await api.createOvpnKey(server);
      if (res.data) {
        setKeyMsg('OpenVPN ключ создан.');
        setCreateKeyOpen(false);
        setVpnProtocol('ovpn');
        setSelectedOvpnServer(server);
        await load();
      } else if (res.error) {
        setKeyErr(res.error.message);
      }
    } finally {
      setOvpnBusy(false);
    }
  };

  const createWgKey = async (serverCode?: string) => {
    const server = (serverCode || selectedWgServer).trim();
    if (!server) return;
    setWgBusy(true);
    setKeyMsg(null);
    setKeyErr(null);
    try {
      const res = await api.createWgKey(server);
      if (res.data) {
        setKeyMsg(`WireGuard ключ создан на ${server.toUpperCase()}.`);
        setCreateKeyOpen(false);
        setVpnProtocol('wg');
        setSelectedWgServer(server);
        await load();
      } else if (res.error) {
        setKeyErr(res.error.message);
      }
    } finally {
      setWgBusy(false);
    }
  };

  const openCreateKeyModal = (protocol: CreateKeyProtocol = vpnProtocol) => {
    setKeyErr(null);
    setCreateKeyProtocol(protocol);
    const servers = protocol === 'wg' ? wgServers : ovpnServers;
    const preferred =
      protocol === 'wg'
        ? selectedWgServer || servers[0] || ''
        : selectedOvpnServer || servers[0] || '';
    setCreateKeyRegion(preferred);
    setCreateKeyOpen(true);
  };

  const deleteWgKey = async (keyId: number) => {
    const confirmed = await confirmAction('Удалить WireGuard ключ?');
    if (!confirmed) return;
    setDeleteBusyId(keyId);
    setKeyMsg(null);
    setKeyErr(null);
    try {
      const res = await api.deleteWgKey(keyId);
      if (res.data?.deleted) {
        setKeyMsg('WireGuard ключ удалён.');
        if (keyConfig?.key_id === keyId) setKeyConfig(null);
        await load();
      } else if (res.error) {
        setKeyErr(res.error.message);
      }
    } finally {
      setDeleteBusyId(null);
    }
  };

  const deleteOvpnKey = async (keyId: number) => {
    const confirmed = await confirmAction('Удалить OpenVPN ключ?');
    if (!confirmed) return;
    setDeleteBusyId(keyId);
    setKeyMsg(null);
    setKeyErr(null);
    try {
      const res = await api.deleteOvpnKey(keyId);
      if (res.data?.deleted) {
        setKeyMsg('OpenVPN ключ удалён.');
        if (keyConfig?.key_id === keyId) setKeyConfig(null);
        await load();
      } else if (res.error) {
        setKeyErr(res.error.message);
      }
    } finally {
      setDeleteBusyId(null);
    }
  };

  const openProfile = (tab: ProfileTab, options?: { scrollTop?: boolean }) => {
    setProfileTab(tab);
    setNav('profile');
    if (options?.scrollTop !== false) {
      window.scrollTo(0, 0);
    }
  };

  const goToRenew = () => {
    openProfile('payments', { scrollTop: false });
    const scrollToRenew = () => {
      const el = document.getElementById('renew-subscription');
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };
    window.setTimeout(() => {
      if (!scrollToRenew()) {
        window.setTimeout(scrollToRenew, 200);
      }
    }, 80);
  };

  useEffect(() => {
    if (loading) return;
    const pending = peekPendingInvite();
    if (!pending) return;
    setPromoCode(pending);
    setInviteFromLink(true);
    openProfile('promo');
  }, [loading]);

  const cancelPaymentCheck = () => {
    paymentPollAbortRef.current = true;
    paymentPollRunningRef.current = false;
    // Do NOT cancel the payment in DB — SBP can still succeed after the user dismisses the spinner.
    setPaymentChecking(false);
    setPayBusy(false);
    setPayMsg(
      getPendingPaymentId()
        ? 'Проверка скрыта. Если оплатили — нажмите «Проверить оплату» или просто подождите, дни начислятся автоматически.'
        : null,
    );
  };

  const checkPendingPaymentNow = () => {
    const pendingId = getPendingPaymentId();
    if (!pendingId) {
      setPayErr('Нет ожидающего платежа. Создайте оплату заново.');
      return;
    }
    setPayErr(null);
    void pollPendingPayment({ switchToPayments: true });
  };

  const startPayment = async () => {
    if (payBusy) return;
    setPayBusy(true);
    setPayMsg(null);
    setPayErr(null);
    paymentPollAbortRef.current = true;
    paymentPollRunningRef.current = false;
    setPaymentChecking(false);
    clearPendingPaymentId();

    const res = await api.createYookassaPayment(selectedPayTier, selectedPayPeriod);
    if (res.data) {
      setPendingPaymentId(res.data.payment_id);
      setPayBusy(false);
      setPayMsg('Открыли страницу оплаты. После оплаты вернитесь сюда — статус обновится сам.');
      // Telegram Mini App: open in system browser so SBP/bank deep links work.
      openExternalUrl(res.data.confirmation_url);
      paymentPollAbortRef.current = false;
      void pollPendingPayment({ switchToPayments: true });
      return;
    }
    setPayErr(res.error?.message ?? 'Не удалось создать платёж');
    setPayBusy(false);
  };

  const redeemPromo = async (e: React.FormEvent) => {
    e.preventDefault();
    setPromoMsg(null);
    setPromoErr(null);
    const res = await api.redeemPromo(promoCode.trim());
    if (res.data) {
      const { added_days, bonus_days, reward_type } = res.data;
      const label = reward_type === 'invite' ? 'Инвайт' : 'Промокод';
      const daysNote =
        added_days !== bonus_days
          ? `+${added_days} дн. (бонус ${bonus_days} дн. конвертирован)`
          : `+${added_days} дн.`;
      setPromoMsg(
        `${label} ${res.data.code}: ${daysNote}, tier ${res.data.subscription_tier}, до ${formatDate(res.data.subscription_until)}`,
      );
      setPromoCode('');
      clearPendingInvite();
      setInviteFromLink(false);
      await refresh();
      await load();
      await loadInvite();
    } else if (res.error) {
      setPromoErr(res.error.message);
    }
  };

  const sub = profile?.subscription;
  const subStatus = sub?.subscription_status ?? 'none';
  const hasVpnKeys = wgKeys.length > 0 || ovpnKeys.length > 0;
  const hasKeyAccess = subStatus === 'keys_only' || subStatus === 'lifetime' || hasVpnKeys;
  const maxPerRegion = sub?.max_keys_per_region ?? 3;
  const maxWgKeys = sub?.max_wg_keys ?? sub?.max_vpn_keys ?? maxPerRegion;
  const maxOvpnKeys = sub?.max_ovpn_keys ?? maxPerRegion;
  const wgTabCount = Math.max(wgKeys.length, sub?.wg_keys_count ?? 0);
  const ovpnTabCount = Math.max(ovpnKeys.length, sub?.ovpn_keys_count ?? 0);
  const keysOutOfSync =
    (sub?.wg_keys_count ?? 0) > wgKeys.length || (sub?.ovpn_keys_count ?? 0) > ovpnKeys.length;
  const createKeyWgOnRegion = wgKeys.filter(
    (k) => k.server_country.toLowerCase() === createKeyRegion.toLowerCase(),
  ).length;
  const regionLabel = (code: string) => {
    const hit = vpnRegions.find((r) => r.code.toLowerCase() === code.toLowerCase());
    return hit?.name || code.toUpperCase();
  };
  const regionCity = (code: string) => {
    const hit = vpnRegions.find((r) => r.code.toLowerCase() === code.toLowerCase());
    return hit?.city || '';
  };
  const keysQuery = keysSearch.trim().toLowerCase();
  const filteredWgKeys = wgKeys.filter((k) => {
    if (keysRegionFilter !== 'all' && k.server_country.toLowerCase() !== keysRegionFilter.toLowerCase()) {
      return false;
    }
    if (!keysQuery) return true;
    const hay = `${k.server_country} ${regionLabel(k.server_country)} ${regionCity(k.server_country)} ${k.client_id} ${k.ip_address ?? ''}`.toLowerCase();
    return hay.includes(keysQuery);
  });
  const filteredOvpnKeys = ovpnKeys.filter((k) => {
    if (keysRegionFilter !== 'all' && k.server_code.toLowerCase() !== keysRegionFilter.toLowerCase()) {
      return false;
    }
    if (!keysQuery) return true;
    const hay = `${k.server_code} ${regionLabel(k.server_code)} ${regionCity(k.server_code)} ${k.profile_id} ${k.ip_address ?? ''}`.toLowerCase();
    return hay.includes(keysQuery);
  });
  const keyFilterRegions = Array.from(
    new Set([
      ...wgKeys.map((k) => k.server_country.toLowerCase()),
      ...ovpnKeys.map((k) => k.server_code.toLowerCase()),
      ...wgServers.map((s) => s.toLowerCase()),
      ...ovpnServers.map((s) => s.toLowerCase()),
    ]),
  ).sort();
  const lastPayment = payments[0];
  const progressPct = subscriptionProgress(sub?.subscription_until, lastPayment?.days ?? 30, subStatus);
  const daysLeft = daysUntil(sub?.subscription_until);
  const subscription = profile?.subscription;
  const selectedTierOffer = offers?.tiers.find((t) => t.tier_id === selectedPayTier);
  const selectedPeriodOffer = selectedTierOffer?.periods.find((p) => p.period === selectedPayPeriod);

  if (!user) return null;

  return (
    <CabinetLayout
      activeNav={nav}
      onNavChange={setNav}
      profileTab={profileTab}
      onProfileTabChange={(tab) => openProfile(tab)}
      userName={displayName}
      userId={user.telegram_user_id}
      userTierLabel={
        subscription?.subscription_tier
          ? `${tierLabel(subscription.subscription_tier)} пользователь`
          : null
      }
      botUrl={botUrl}
      adminPanelUrl={authConfig?.admin_panel_url}
      showAdminEntry={isAdmin}
      onLogout={() => logout()}
      onCopyId={copyUserId}
      idCopied={idCopied}
    >
      <SupportNotifyBridge
        enabled
        activeNav={nav}
        openSupport={openSupportTicket}
        onToast={showSupportFlash}
      />
      {supportFlash && nav !== 'support' ? (
        <p
          className="support-flash"
          role="status"
          onClick={() => {
            const id = supportFlash.ticketId;
            setSupportFlash(null);
            openSupportTicket(id);
            requestOpenSupportTicket(id, true);
          }}
          style={{ cursor: 'pointer' }}
        >
          {supportFlash.msg}
        </p>
      ) : null}
      {loading ? (
        <SplashScreen label="Загрузка кабинета" />
      ) : (
      <div className="content-enter">
      {loadError && (
        <div className="card section-block">
          <p className="error-text">{loadError}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              void (async () => {
                await ensurePortalSession();
                await refresh();
                await load();
              })()
            }
          >
            Повторить
          </button>
        </div>
      )}

      {onceCreds ? (
        <div className="card section-block" style={{ borderColor: 'rgba(59, 130, 246, 0.45)' }}>
          <h3 style={{ marginTop: 0 }}>Доступ к сайту</h3>
          <p className="muted" style={{ fontSize: '0.875rem' }}>
            Сохраните логин и пароль — пароль показывается один раз. Потом его можно сменить в настройках
            без ввода старого.
          </p>
          <div className="settings-row">
            <span className="muted">Логин</span>
            <code>{onceCreds.username}</code>
          </div>
          <div className="settings-row">
            <span className="muted">Пароль</span>
            <code>{onceCreds.password}</code>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                void navigator.clipboard?.writeText(
                  `Логин: ${onceCreds.username}\nПароль: ${onceCreds.password}`,
                );
              }}
            >
              Скопировать
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                clearOnceCredentials();
                setOnceCreds(null);
              }}
            >
              Понятно, скрыть
            </button>
          </div>
        </div>
      ) : null}

      {nav === 'home' && profile && subscription && (
        <>
          <div
            className={`home-welcome card ${
              subStatus === 'expired'
                ? 'home-welcome--expired'
                : subStatus === 'none'
                  ? 'home-welcome--none'
                  : 'home-welcome--active'
            }`}
          >
            <IconCrown size={150} className="home-welcome-watermark" />
            <div className="home-welcome-body">
              <div className="home-welcome-main">
                <div className="home-welcome-top">
                <span className="home-welcome-icon">
                  <IconCrown size={36} />
                </span>
                  <div className="home-welcome-copy">
                    <p className="home-welcome-greet">Здравствуйте, {displayName}</p>
                    <div className="home-welcome-title-row">
                      <h2 className="home-welcome-tier">
                        {subscription.subscription_tier
                          ? tierLabel(subscription.subscription_tier)
                          : subStatus === 'keys_only'
                            ? 'По ключам'
                            : 'Подписка'}
                      </h2>
                      <span className="home-welcome-status">
                        <span className="home-welcome-status-dot" />
                        {subStatusLabel(subscription.subscription_status)}
                      </span>
                    </div>
                    <div className="home-welcome-until">
                      {subscriptionUntilCopy(subscription.subscription_until, subStatus)}
                    </div>
                  </div>
                </div>

                {subStatus !== 'lifetime' && subStatus !== 'keys_only' && subStatus !== 'none' ? (
                  <div className="home-welcome-progress">
                    <div className="home-welcome-progress-track">
                      <div
                        className="home-welcome-progress-fill"
                        style={{
                          width: `${Math.max(progressPct > 0 ? 8 : 0, Math.min(100, progressPct))}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="home-welcome-actions">
                  <button
                    type="button"
                    className="home-welcome-btn home-welcome-btn-primary"
                    onClick={subStatus === 'lifetime' ? () => openProfile('vpn') : goToRenew}
                  >
                    <IconRefresh size={16} />
                    {subStatus === 'lifetime'
                      ? 'Управление ключами'
                      : subStatus === 'keys_only'
                        ? 'Продлить подписку'
                        : 'Продлить подписку'}
                  </button>
                  <button
                    type="button"
                    className="home-welcome-btn home-welcome-btn-ghost"
                    onClick={subStatus === 'keys_only' ? () => openProfile('vpn') : goToRenew}
                  >
                    <IconCreditCard size={16} />
                    {subStatus === 'keys_only' ? 'Управление ключами' : 'Сменить тариф'}
                  </button>
                </div>
              </div>

              <div className="home-welcome-days">
                <span className="home-welcome-days-icon" aria-hidden>
                  <IconCalendar size={22} />
                </span>
                <div className="home-welcome-days-text">
                  <span className="home-welcome-days-label">
                    {subStatus === 'keys_only' ? 'Статус' : 'Осталось'}
                  </span>
                  <strong>
                    {subStatus === 'lifetime'
                      ? 'Бессрочно'
                      : subStatus === 'keys_only'
                        ? 'Нужно продлить'
                        : daysLeft == null
                          ? '—'
                          : `${daysLeft} ${pluralDays(daysLeft)}`}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <div className="quick-actions-section">
            <h3 className="quick-actions-heading">Быстрые действия</h3>
            <div className="quick-actions">
              <button
                type="button"
                className="quick-action quick-action-blue"
                disabled={downloadBusy || (wgKeys.length === 0 && ovpnKeys.length === 0)}
                onClick={openDownloadPicker}
              >
                <span className="quick-action-icon blue">
                  <IconDownload size={18} />
                </span>
                <span className="quick-action-text">
                  <span className="quick-action-label">
                    {downloadBusy ? 'Скачивание…' : 'Скачать'}
                  </span>
                </span>
              </button>
              <button
                type="button"
                className="quick-action quick-action-green"
                onClick={() => openProfile('vpn')}
              >
                <span className="quick-action-icon green">
                  <IconPlus size={18} />
                </span>
                <span className="quick-action-text">
                  <span className="quick-action-label">Добавить</span>
                </span>
              </button>
              <button
                type="button"
                className="quick-action quick-action-purple"
                onClick={goToRenew}
              >
                <span className="quick-action-icon purple">
                  <IconCreditCard size={18} />
                </span>
                <span className="quick-action-text">
                  <span className="quick-action-label">Продлить</span>
                </span>
              </button>
              <button
                type="button"
                className="quick-action quick-action-blue"
                onClick={() => setNav('help')}
              >
                <span className="quick-action-icon blue">
                  <IconDoc size={18} />
                </span>
                <span className="quick-action-text">
                  <span className="quick-action-label">Инструкция</span>
                </span>
              </button>
              <button
                type="button"
                className="quick-action quick-action-neutral quick-action-wide"
                onClick={() => {
                  requestOpenSupportCreate();
                  setNav('support');
                }}
              >
                <span className="quick-action-icon neutral">
                  <IconHelp size={18} />
                </span>
                <span className="quick-action-text">
                  <span className="quick-action-label">Поддержка</span>
                </span>
              </button>
            </div>
          </div>
        </>
      )}

      {nav === 'profile' && (
        <div className="profile-subnav" role="tablist" aria-label="Разделы профиля">
          {PROFILE_TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={profileTab === id}
              className={`profile-subnav-item ${profileTab === id ? 'active' : ''}`}
              onClick={() => setProfileTab(id)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}

      {nav === 'profile' && profileTab === 'subscription' && profile && subscription && (
        <>
          {subStatus === 'keys_only' && (
            <div className="card section-block" style={{ marginBottom: '1rem' }}>
              <p style={{ margin: 0 }}>
                У вас активные ключи без платной подписки. Скачивайте конфиги во вкладке{' '}
                <button type="button" className="btn btn-ghost btn-inline-link" onClick={() => openProfile('vpn')}>
                  Ключи
                </button>
                .
              </p>
            </div>
          )}

          <div className="sub-dash">
            <div
              className={`sub-hero ${
                subStatus === 'expired'
                  ? 'sub-hero--expired'
                  : subStatus === 'none'
                    ? 'sub-hero--none'
                    : 'sub-hero--active'
              }`}
            >
              <div className="sub-hero-top">
                <span className="sub-hero-icon">
                  <IconCrown size={48} />
                </span>
                <div className="sub-hero-copy">
                  <div className="sub-hero-title-row">
                    <h2 className="sub-hero-tier">
                      {subscription.subscription_tier
                        ? tierLabel(subscription.subscription_tier)
                        : subStatus === 'keys_only'
                          ? 'По ключам'
                          : 'Подписка'}
                    </h2>
                    <span className="sub-hero-status">
                      <span className="sub-hero-status-dot" />
                      {subStatusLabel(subscription.subscription_status)}
                    </span>
                  </div>
                  <div className="sub-hero-until">
                    <IconCalendar size={15} />
                    <span>{subscriptionUntilCopy(subscription.subscription_until, subStatus)}</span>
                  </div>
                </div>
              </div>
              <IconCrown size={160} className="sub-hero-watermark" />

              <div className="sub-hero-progress">
                <div className="sub-hero-progress-track">
                  <div
                    className="sub-hero-progress-fill"
                    style={{ width: `${Math.max(progressPct > 0 ? 8 : 0, Math.min(100, progressPct))}%` }}
                  />
                </div>
                <div className="sub-hero-days">
                  <IconCrown size={15} />
                  <span>{subscriptionDaysLeftLabel(subscription.subscription_until, subStatus)}</span>
                </div>
              </div>

              <div className="sub-hero-actions">
                <button
                  type="button"
                  className="sub-hero-btn sub-hero-btn-primary"
                  onClick={subStatus === 'lifetime' ? () => openProfile('vpn') : goToRenew}
                >
                  <IconCalendar size={16} />
                  {subStatus === 'lifetime' ? 'Управление ключами' : 'Продлить подписку'}
                </button>
                <button
                  type="button"
                  className="sub-hero-btn sub-hero-btn-ghost"
                  onClick={subStatus === 'keys_only' ? () => openProfile('vpn') : goToRenew}
                >
                  {subStatus === 'keys_only' ? 'Управление ключами' : 'Сменить тариф'}
                </button>
              </div>
            </div>

            <button type="button" className="sub-proto-card sub-proto-wg" onClick={() => openProfile('vpn')}>
              <div className="sub-proto-head">
                <span className="sub-proto-icon wg">
                  <IconShield size={18} />
                </span>
                <div className="sub-proto-name wg">WireGuard</div>
              </div>
              <div className="sub-proto-count">
                <strong>{subscription.wg_keys_count ?? wgKeys.length}</strong>
                <span>{pluralDevices(subscription.wg_keys_count ?? wgKeys.length)}</span>
              </div>
              <span className="sub-proto-link">Управление →</span>
            </button>

            <button type="button" className="sub-proto-card sub-proto-ovpn" onClick={() => openProfile('vpn')}>
              <div className="sub-proto-head">
                <span className="sub-proto-icon ovpn">
                  <IconKey size={18} />
                </span>
                <div className="sub-proto-name ovpn">OpenVPN</div>
              </div>
              <div className="sub-proto-count">
                <strong>{subscription.ovpn_keys_count ?? ovpnKeys.length}</strong>
                <span>{pluralProfiles(subscription.ovpn_keys_count ?? ovpnKeys.length)}</span>
              </div>
              <span className="sub-proto-link">Управление →</span>
            </button>

            <div className="sub-panel sub-ops">
              <div className="sub-panel-head">
                <div className="sub-panel-title">
                  <span className="sub-panel-title-icon">
                    <IconRefresh size={18} />
                  </span>
                  <h3>Последняя операция</h3>
                </div>
                <button type="button" className="sub-panel-link" onClick={() => openProfile('payments')}>
                  Вся история →
                </button>
              </div>

              {lastPayment ? (
                <div
                  className={`sub-ops-card ${
                    lastPayment.status === 'processed' || lastPayment.status === 'success'
                      ? 'is-success'
                      : paymentStatusIconIsError(lastPayment.status)
                        ? 'is-error'
                        : 'is-muted'
                  }`}
                >
                  <div className="sub-ops-banner">
                    <span className="sub-ops-status-icon">
                      {paymentStatusIconIsError(lastPayment.status) ? (
                        <IconX size={16} />
                      ) : (
                        <IconCheck size={16} />
                      )}
                    </span>
                    <span>{paymentHeadline(lastPayment.status)}</span>
                  </div>
                  <div className="sub-ops-main">
                    <div className="sub-ops-amount">{formatMoney(lastPayment.amount)}</div>
                    <span className="sub-ops-sep" aria-hidden />
                    <div className="sub-ops-details">
                      <strong>{tierLabel(lastPayment.subscription_tier)}</strong>
                      <span className="sub-ops-date">
                        <IconCalendar size={14} />
                        {formatDateLong(lastPayment.created_at)}
                      </span>
                    </div>
                    <span className={`sub-ops-badge ${paymentStatusBadgeClass(lastPayment.status)}`}>
                      {paymentStatusLabel(lastPayment.status)}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  Платежей пока нет
                </p>
              )}
            </div>

            <div className="sub-panel sub-guides">
              <div className="sub-panel-head">
                <div className="sub-panel-title">
                  <span className="sub-panel-title-icon">
                    <IconDevice size={18} />
                  </span>
                  <div>
                    <h3>Инструкции</h3>
                    <p className="sub-panel-subtitle">Пошаговые настройки VPN для всех устройств</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="sub-panel-link"
                  onClick={() => {
                    prepareHelpScreen({ name: 'hub' });
                    setNav('help');
                  }}
                >
                  Все инструкции →
                </button>
              </div>

              <div className="sub-guide-grid">
                {(
                  [
                    { id: 'windows', label: 'Windows', Icon: IconWindows, tone: 'windows', screen: { name: 'os' as const, os: 'windows' as const } },
                    { id: 'macos', label: 'macOS', Icon: IconApple, tone: 'apple', screen: { name: 'os' as const, os: 'macos' as const } },
                    { id: 'android', label: 'Android', Icon: IconAndroid, tone: 'android', screen: { name: 'os' as const, os: 'android' as const } },
                    { id: 'ios', label: 'iPhone', Icon: IconApple, tone: 'apple', screen: { name: 'os' as const, os: 'ios' as const } },
                    { id: 'linux', label: 'Linux', Icon: IconLinux, tone: 'linux', screen: { name: 'device' as const, device: 'computer' as const } },
                    { id: 'routers', label: 'Роутеры', Icon: IconRouter, tone: 'router', screen: { name: 'hub' as const } },
                  ] as const
                ).map(({ id, label, Icon, tone, screen }) => (
                  <button
                    key={id}
                    type="button"
                    className="sub-guide-item"
                    onClick={() => {
                      prepareHelpScreen(screen);
                      setNav('help');
                    }}
                  >
                    <span className={`sub-guide-icon ${tone}`}>
                      <Icon size={26} />
                    </span>
                    <span className="sub-guide-label">{label}</span>
                    <span className="sub-guide-arrow" aria-hidden>
                      →
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {nav === 'profile' && profileTab === 'vpn' && (
        <div className="keys-page">
          <RegionDiagnosticsPanel
            deviceLimit={Math.max(maxWgKeys, maxOvpnKeys, maxPerRegion)}
            selectedCode={
              createKeyOpen
                ? createKeyRegion
                : vpnProtocol === 'wg'
                  ? selectedWgServer
                  : selectedOvpnServer
            }
            onSelectRegion={(code) => {
              if (wgServers.some((s) => s.toLowerCase() === code.toLowerCase())) {
                setSelectedWgServer(code);
              }
              if (ovpnServers.some((s) => s.toLowerCase() === code.toLowerCase())) {
                setSelectedOvpnServer(code);
              }
              if (createKeyOpen) setCreateKeyRegion(code);
            }}
          />

          <div className="keys-panel">
            <div className="keys-panel-toolbar">
              <div className="keys-protocol-tabs" role="tablist" aria-label="Протокол">
                <button
                  type="button"
                  role="tab"
                  aria-selected={vpnProtocol === 'wg'}
                  className={`keys-protocol-tab ${vpnProtocol === 'wg' ? 'is-active' : ''}`}
                  onClick={() => setVpnProtocol('wg')}
                >
                  <IconShield size={16} />
                  WireGuard ({wgTabCount})
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={vpnProtocol === 'ovpn'}
                  className={`keys-protocol-tab ${vpnProtocol === 'ovpn' ? 'is-active' : ''}`}
                  onClick={() => setVpnProtocol('ovpn')}
                >
                  <IconLock size={16} />
                  OpenVPN ({ovpnTabCount})
                </button>
              </div>
              <button
                type="button"
                className="btn btn-primary keys-create-btn"
                onClick={() => openCreateKeyModal(vpnProtocol)}
                disabled={
                  (vpnProtocol === 'wg' ? wgServers.length === 0 : ovpnServers.length === 0) &&
                  !refreshBusy
                }
              >
                <IconPlus size={16} />
                Создать ключ
              </button>
            </div>

            <div className="keys-panel-filters">
              <h3 className="keys-panel-title">Список ключей</h3>
              <div className="keys-filters-controls">
                <label className="keys-search">
                  <IconSearch size={16} />
                  <input
                    type="search"
                    value={keysSearch}
                    onChange={(e) => setKeysSearch(e.target.value)}
                    placeholder="Поиск по ключам..."
                    aria-label="Поиск по ключам"
                  />
                </label>
                <label className="keys-region-filter">
                  <IconGlobe size={16} />
                  <select
                    value={keysRegionFilter}
                    onChange={(e) => setKeysRegionFilter(e.target.value)}
                    aria-label="Фильтр по региону"
                  >
                    <option value="all">Все регионы</option>
                    {keyFilterRegions.map((code) => (
                      <option key={code} value={code}>
                        {regionLabel(code)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {keysOutOfSync && (
              <p className="error-text" style={{ marginTop: 0 }}>
                Список ключей мог устареть. Обновите страницу или создайте ключ заново.
              </p>
            )}
            {keyMsg && <p className="success-text">{keyMsg}</p>}
            {keyErr && !createKeyOpen && <p className="error-text">{keyErr}</p>}

            {vpnProtocol === 'wg' && (
              <div className="keys-table-wrap">
                {filteredWgKeys.length === 0 ? (
                  <p className="muted keys-empty">
                    {wgKeys.length === 0
                      ? wgServers.length === 0 && !hasVpnKeys
                        ? 'Нет доступных WireGuard серверов для вашего тарифа.'
                        : 'WireGuard ключей пока нет.'
                      : 'Ничего не найдено по фильтру.'}
                  </p>
                ) : (
                  <table className="keys-table">
                    <thead>
                      <tr>
                        <th>Страна / Регион</th>
                        <th>IP-адрес</th>
                        <th>Статус</th>
                        <th>Дата создания</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWgKeys.map((k) => (
                        <Fragment key={k.id}>
                          <tr>
                            <td>
                              <div className="keys-region-cell">
                                <RegionFlag code={k.server_country} label={regionLabel(k.server_country)} size={34} />
                                <div>
                                  <div className="keys-region-code">{k.server_country.toUpperCase()}</div>
                                  <div className="keys-region-name muted">
                                    {regionLabel(k.server_country)}
                                    {regionCity(k.server_country)
                                      ? ` (${regionCity(k.server_country)})`
                                      : ''}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="keys-ip" title={k.ip_address || undefined}>
                              {k.ip_address || '—'}
                            </td>
                            <td>
                              <span className="keys-status is-active">
                                <span className="keys-status-dot" />
                                Активен
                              </span>
                            </td>
                            <td className="keys-date">{formatDateShort(k.created_at)}</td>
                            <td>
                              <div className="keys-row-actions">
                                <button
                                  type="button"
                                  className="keys-action keys-action-config"
                                  disabled={configBusyId === k.id || deleteBusyId === k.id}
                                  onClick={() => loadWgConfig(k.id)}
                                >
                                  <IconDownload size={14} />
                                  {configBusyId === k.id ? '…' : 'Конфиг'}
                                </button>
                                <button
                                  type="button"
                                  className="keys-action keys-action-delete"
                                  disabled={configBusyId === k.id || deleteBusyId === k.id}
                                  onClick={() => deleteWgKey(k.id)}
                                >
                                  <IconTrash size={14} />
                                  {deleteBusyId === k.id ? '…' : 'Удалить'}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {keyConfig?.key_id === k.id && keyConfig.protocol !== 'ovpn' && (
                            <tr className="keys-config-row">
                              <td colSpan={5}>
                                <div className="keys-config-panel">
                                  <div className="keys-config-actions">
                                    <button
                                      type="button"
                                      className="keys-action keys-action-config"
                                      onClick={() => void downloadCurrentConfig()}
                                    >
                                      <IconDownload size={14} />
                                      Скачать .conf
                                    </button>
                                    <button
                                      type="button"
                                      className="keys-action keys-action-ghost"
                                      onClick={() => setShowConfigText((v) => !v)}
                                    >
                                      {showConfigText ? 'Скрыть текст' : 'Показать текст'}
                                    </button>
                                  </div>
                                  <div className="keys-config-apps">
                                    <div className="keys-config-apps-title">
                                      Шаг 1: скачайте клиент WireGuard
                                    </div>
                                    <div className="keys-config-apps-grid">
                                      {vpnAppLinks('wg').map((app) => (
                                        <a
                                          key={app.id}
                                          className="keys-app-link"
                                          href={app.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {app.label}
                                          <span aria-hidden>↗</span>
                                        </a>
                                      ))}
                                    </div>
                                    <p className="keys-config-apps-hint muted">
                                      Шаг 2: скачайте файл .conf и импортируйте его в приложении.
                                    </p>
                                  </div>
                                  {showConfigText && (
                                    <pre className="keys-config-block">{keyConfig.config_text}</pre>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {vpnProtocol === 'ovpn' && (
              <div className="keys-table-wrap">
                {filteredOvpnKeys.length === 0 ? (
                  <p className="muted keys-empty">
                    {ovpnKeys.length === 0
                      ? ovpnServers.length === 0
                        ? 'Нет доступных OpenVPN серверов для вашего тарифа.'
                        : 'OpenVPN ключей пока нет.'
                      : 'Ничего не найдено по фильтру.'}
                  </p>
                ) : (
                  <table className="keys-table">
                    <thead>
                      <tr>
                        <th>Страна / Регион</th>
                        <th>IP-адрес</th>
                        <th>Статус</th>
                        <th>Дата создания</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOvpnKeys.map((k) => {
                        const expired =
                          Boolean(k.expires_at) && new Date(k.expires_at!).getTime() < Date.now();
                        return (
                          <Fragment key={k.id}>
                            <tr>
                              <td>
                                <div className="keys-region-cell">
                                  <RegionFlag code={k.server_code} label={regionLabel(k.server_code)} size={34} />
                                  <div>
                                    <div className="keys-region-code">{k.server_code.toUpperCase()}</div>
                                    <div className="keys-region-name muted">
                                      {regionLabel(k.server_code)}
                                      {regionCity(k.server_code)
                                        ? ` (${regionCity(k.server_code)})`
                                        : ''}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="keys-ip" title={k.ip_address || undefined}>
                                {k.ip_address || '—'}
                              </td>
                              <td>
                                <span className={`keys-status ${expired ? 'is-inactive' : 'is-active'}`}>
                                  <span className="keys-status-dot" />
                                  {expired ? 'Не активен' : 'Активен'}
                                </span>
                              </td>
                              <td className="keys-date">{formatDateShort(k.created_at)}</td>
                              <td>
                                <div className="keys-row-actions">
                                  <button
                                    type="button"
                                    className="keys-action keys-action-config"
                                    disabled={configBusyId === k.id || deleteBusyId === k.id}
                                    onClick={() => loadOvpnConfig(k.id)}
                                  >
                                    <IconDownload size={14} />
                                    {configBusyId === k.id ? '…' : 'Конфиг'}
                                  </button>
                                  <button
                                    type="button"
                                    className="keys-action keys-action-delete"
                                    disabled={configBusyId === k.id || deleteBusyId === k.id}
                                    onClick={() => deleteOvpnKey(k.id)}
                                  >
                                    <IconTrash size={14} />
                                    {deleteBusyId === k.id ? '…' : 'Удалить'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {keyConfig?.key_id === k.id && keyConfig.protocol === 'ovpn' && (
                              <tr className="keys-config-row">
                                <td colSpan={5}>
                                  <div className="keys-config-panel">
                                    {(isIosLikeDevice() || isAndroidDevice()) && (
                                      <p className="keys-config-hint muted">
                                        {isIosLikeDevice()
                                          ? 'На iPhone: сохраните файл в «Файлы», затем откройте его через OpenVPN Connect.'
                                          : 'На Android: после скачивания откройте файл в OpenVPN Connect.'}
                                      </p>
                                    )}
                                    <div className="keys-config-actions">
                                      <button
                                        type="button"
                                        className="keys-action keys-action-config"
                                        onClick={() => void downloadCurrentConfig()}
                                      >
                                        <IconDownload size={14} />
                                        Скачать .ovpn
                                      </button>
                                      <button
                                        type="button"
                                        className="keys-action keys-action-ghost"
                                        onClick={() => setShowConfigText((v) => !v)}
                                      >
                                        {showConfigText ? 'Скрыть текст' : 'Показать текст'}
                                      </button>
                                    </div>
                                    <div className="keys-config-apps">
                                      <div className="keys-config-apps-title">
                                        Шаг 1: скачайте OpenVPN Connect
                                      </div>
                                      <div className="keys-config-apps-grid">
                                        {vpnAppLinks('ovpn').map((app) => (
                                          <a
                                            key={app.id}
                                            className="keys-app-link"
                                            href={app.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {app.label}
                                            <span aria-hidden>↗</span>
                                          </a>
                                        ))}
                                      </div>
                                      <p className="keys-config-apps-hint muted">
                                        Шаг 2: скачайте файл .ovpn и импортируйте его в приложении.
                                      </p>
                                    </div>
                                    {showConfigText && (
                                      <pre className="keys-config-block">{keyConfig.config_text}</pre>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          <div className="vpn-help-banner">
            <span className="vpn-help-icon">
              <IconInfo size={20} />
            </span>
            <p className="vpn-help-text">
              Нужна помощь? Если у вас возникли вопросы по настройке VPN — ознакомьтесь с инструкциями
              или обратитесь в поддержку.
            </p>
            <button
              type="button"
              className="btn vpn-help-btn"
              onClick={() => {
                prepareHelpScreen({ name: 'hub' });
                setNav('help');
              }}
            >
              <IconBook size={16} />
              Инструкции
            </button>
          </div>
        </div>
      )}

      {nav === 'profile' && profileTab === 'payments' && (
        <>
          <div id="renew-subscription" className="card section-block renew-section">
            <h3 style={{ marginTop: 0 }}>Продлить подписку</h3>
            <p className="muted" style={{ fontSize: '0.875rem', marginTop: 0 }}>
              Оплата через ЮKassa — карты, СБП и другие способы.
            </p>

            {paymentChecking && (
              <p className="muted" style={{ marginBottom: '0.75rem' }}>
                Проверяем статус оплаты… Можно спокойно оплатить в открывшемся окне и вернуться сюда.
              </p>
            )}
            {(paymentChecking || getPendingPaymentId()) && (
              <div className="pay-check-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={paymentChecking}
                  onClick={checkPendingPaymentNow}
                >
                  Проверить оплату
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={cancelPaymentCheck}
                >
                  Скрыть проверку
                </button>
              </div>
            )}
            {payMsg && <p className="success-text">{payMsg}</p>}
            {payErr && <p className="error-text">{payErr}</p>}

            {offers && offers.tiers.length > 0 ? (
              <>
                <div className="pay-tier-tabs">
                  {offers.tiers.map((tier) => (
                    <button
                      key={tier.tier_id}
                      type="button"
                      className={`btn pay-tier-tab ${selectedPayTier === tier.tier_id ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => {
                        setSelectedPayTier(tier.tier_id);
                        const month = tier.periods.find((p) => p.period === '1')?.period;
                        setSelectedPayPeriod(month || tier.periods[0]?.period || '1');
                      }}
                    >
                      <span className="pay-tier-tab-icon" aria-hidden>
                        {tier.tier_id === 'premium' ? <IconCrown size={18} /> : <IconStar size={18} />}
                      </span>
                      {tier.name}
                    </button>
                  ))}
                </div>

                {selectedTierOffer && (
                  <div className="pay-period-grid">
                    {selectedTierOffer.periods.map((p) => {
                      const discount = periodDiscountPercent(p);
                      return (
                        <button
                          key={p.period}
                          type="button"
                          className={`pay-period-option ${selectedPayPeriod === p.period ? 'active' : ''}`}
                          onClick={() => setSelectedPayPeriod(p.period)}
                        >
                          {discount != null && (
                            <span className={`pay-period-discount${p.intro ? ' is-intro' : ''}`}>
                              −{discount}%
                            </span>
                          )}
                          <span className="pay-period-glyph" aria-hidden>
                            <span className="pay-period-glyph-num">{p.period}</span>
                          </span>
                          <span className="pay-period-label">{p.label}</span>
                          <span className="pay-period-price">
                            {formatMoney(p.price, p.currency)}
                          </span>
                          {p.intro && p.regular_price ? (
                            <span className="pay-period-was muted">
                              вместо {formatMoney(p.regular_price, p.currency)}
                            </span>
                          ) : null}
                          <span className="pay-period-days muted">{p.days} дн.</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {tierChangePreview && (
                  <div className="pay-tier-change-notice" role="status">
                    <div className="pay-tier-change-grid">
                      <div className="pay-tier-change-col">
                        <p className="pay-tier-change-title">
                          <span className="pay-tier-change-icon" aria-hidden>
                            {tierChangePreview.new_tier === 'premium' ? (
                              <IconCrown size={18} />
                            ) : (
                              <IconStar size={18} />
                            )}
                          </span>
                          Смена уровня подписки
                        </p>
                        <p className="muted pay-tier-change-meta">
                          Сейчас: {tierLabel(tierChangePreview.current_tier)} — осталось{' '}
                          {tierChangePreview.remaining_days} дн.
                          <br />
                          Покупка: {tierLabel(tierChangePreview.new_tier)} —{' '}
                          {tierChangePreview.purchased_days} дн.
                        </p>
                      </div>
                      <div className="pay-tier-change-col">
                        <p className="pay-tier-change-meta">
                          Остаток пересчитается по стоимости дня:{' '}
                          {tierChangePreview.remaining_days} дн.{' '}
                          {tierLabel(tierChangePreview.current_tier)} →{' '}
                          <strong>
                            {tierChangePreview.converted_days} дн.{' '}
                            {tierLabel(tierChangePreview.new_tier)}
                          </strong>
                        </p>
                        <p className="pay-tier-change-meta" style={{ marginBottom: 0 }}>
                          После оплаты:{' '}
                          <strong>
                            {tierChangePreview.total_days} дн. {tierLabel(tierChangePreview.new_tier)}
                          </strong>
                        </p>
                      </div>
                    </div>
                    {tierChangePreview.is_downgrade && (
                      <p className="pay-tier-change-warn">
                        <IconAlert size={16} />
                        Ключи на серверах Premium (Латвия) будут отключены.
                      </p>
                    )}
                  </div>
                )}

                <div className="pay-summary">
                  {selectedPeriodOffer ? (
                    <p style={{ margin: '0 0 1rem' }}>
                      К оплате:{' '}
                      <strong>{formatMoney(selectedPeriodOffer.price, selectedPeriodOffer.currency)}</strong>
                      {' · '}
                      {tierLabel(selectedPayTier)} · {selectedPeriodOffer.days} дн.
                    </p>
                  ) : (
                    <p className="muted">Выберите период</p>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary btn-block"
                    disabled={payBusy || !selectedPeriodOffer}
                    onClick={startPayment}
                  >
                    {payBusy ? 'Переход к оплате…' : 'Оплатить'}
                  </button>
                </div>
              </>
            ) : (
              <p className="muted">
                {hasKeyAccess
                  ? 'Оплата не требуется — у вас уже есть активные ключи. Новую подписку можно оформить в боте.'
                  : 'Тарифы временно недоступны. Попробуйте позже или оформите подписку в Telegram-боте.'}
              </p>
            )}
          </div>

          <h3 className="payments-history-title">История платежей</h3>
          <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Сумма</th>
                <th>Дней</th>
                <th>Тариф</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Платежей пока нет
                  </td>
                </tr>
              )}
              {payments.map((p) => (
                <tr key={p.id}>
                  <td>{formatDate(p.created_at)}</td>
                  <td>{formatMoney(p.amount)}</td>
                  <td>{p.days}</td>
                  <td>{tierLabel(p.subscription_tier)}</td>
                  <td>
                    <span className={paymentStatusBadgeClass(p.status)}>
                      {paymentStatusLabel(p.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {nav === 'profile' && profileTab === 'promo' && (
        <div className="promo-grid">
          <div className="card promo-card promo-redeem-card">
            <div className="promo-redeem-glow" aria-hidden />
            <img
              className="promo-redeem-gift"
              src={promoIconUrl(PROMO_ICONS.gift3d)}
              alt=""
              aria-hidden
              draggable={false}
            />
            <h3 className="promo-card-title">
              <img
                className="promo-card-title-icon"
                src={promoIconUrl(PROMO_ICONS.tags)}
                alt=""
                aria-hidden
              />
              Промокод или инвайт
            </h3>
            <p className="muted promo-card-desc">
              {inviteFromLink
                ? 'Код из ссылки подставлен — нажмите «Применить».'
                : 'Введите промокод или код приглашения от друга.'}
            </p>
            <form className="promo-redeem-form" onSubmit={redeemPromo}>
              <input
                className="input"
                placeholder="Введите код"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  setInviteFromLink(false);
                }}
                required
              />
              <button type="submit" className="btn btn-primary">
                Применить
              </button>
            </form>
            {promoMsg && <p className="success-text promo-feedback">{promoMsg}</p>}
            {promoErr && <p className="error-text promo-feedback">{promoErr}</p>}
          </div>

          <div className="card promo-card referral-card">
            <h3 className="promo-card-title">
              <img
                className="promo-card-title-icon"
                src={promoIconUrl(PROMO_ICONS.users2)}
                alt=""
                aria-hidden
              />
              Пригласить друга
            </h3>
            {invite ? (
              <>
                <div className="referral-code-box">
                  <div className="referral-code-main">
                    <span className="referral-code-label">Ваш личный код</span>
                    <code className="referral-code-value">{invite.code}</code>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost referral-code-copy"
                    onClick={() => void copyReferralCode()}
                    title="Скопировать код"
                  >
                    <img src={promoIconUrl(PROMO_ICONS.copy)} alt="" aria-hidden />
                    {codeCopied ? 'Скопировано' : 'Скопировать'}
                  </button>
                </div>

                <p className="muted referral-hint">
                  Передайте код другу — он получит +{invite.bonus_days} дн. ({tierLabel(invite.subscription_tier)}).
                  За каждую оплату друга вы получите бонус по правилам реферальной программы (зависит от тарифов).
                  Первый активированный пользовательский код закрепляет реферера навсегда.
                </p>

                {invite.share_url && (
                  <div className="referral-share-block">
                    <span className="referral-code-label">Ссылка-приглашение</span>
                    <code className="referral-share-url">{invite.share_url}</code>
                  </div>
                )}

                <div className="referral-actions">
                  {invite.share_url && (
                    <button
                      type="button"
                      className="btn btn-primary referral-action-btn"
                      onClick={() => void copyReferralLink()}
                    >
                      <img
                        className="promo-btn-icon"
                        src={promoIconUrl(PROMO_ICONS.copyUrl)}
                        alt=""
                        aria-hidden
                      />
                      {linkCopied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost referral-action-btn"
                    disabled={Boolean(invite.days_until_change_allowed)}
                    onClick={() => {
                      setNewReferralCode(invite.code);
                      setShowReferralEdit(true);
                      setReferralMsg(null);
                      setReferralErr(null);
                    }}
                  >
                    <img
                      className="promo-btn-icon"
                      src={promoIconUrl(PROMO_ICONS.edit)}
                      alt=""
                      aria-hidden
                    />
                    Изменить код
                  </button>
                </div>

                {invite.days_until_change_allowed ? (
                  <p className="muted referral-cooldown">
                    Сменить код можно через {invite.days_until_change_allowed}{' '}
                    {pluralDays(invite.days_until_change_allowed)}.
                  </p>
                ) : (
                  <p className="muted referral-cooldown">
                    Хотите свой вариант? Нажмите «Изменить код» (доступно раз в 30 дней).
                  </p>
                )}

                {referralMsg && <p className="success-text">{referralMsg}</p>}

                <div className="referral-banner-block">
                  <div className="referral-banner-split">
                    <div className="referral-banner-info">
                      <div className="referral-banner-head">
                        <h4 className="referral-banner-title">
                          <img
                            className="promo-card-title-icon"
                            src={promoIconUrl(PROMO_ICONS.computer)}
                            alt=""
                            aria-hidden
                          />
                          Баннер для OBS
                        </h4>
                      </div>
                      <p className="muted referral-hint">
                        Добавь в OBS как Browser Source (600×160, прозрачный фон). После смены
                        настроек скопируй URL снова.
                      </p>
                      <ul className="referral-banner-points">
                        <li>Показывает промокод и бонусные дни на стриме</li>
                        <li>Карусель или одна строка — настраивается</li>
                        <li>Цвета и появление можно менять под оформление</li>
                      </ul>
                      <code className="referral-share-url">{resolvedBannerUrl}</code>
                    </div>

                    <BannerLivePreview
                      code={invite.code}
                      bonusDays={invite.bonus_days}
                      opts={bannerOpts}
                      framed
                      pace="preview"
                      className="banner-preview-in-split"
                    />

                    <button
                      type="button"
                      className="btn btn-soft referral-action-btn referral-banner-copy-btn"
                      onClick={() => void copyBannerLink()}
                    >
                      <img
                        className="promo-btn-icon"
                        src={promoIconUrl(PROMO_ICONS.copy2)}
                        alt=""
                        aria-hidden
                      />
                      {bannerCopied ? 'Ссылка баннера скопирована' : 'Скопировать баннер'}
                    </button>

                    <button
                      type="button"
                      className="btn btn-ghost referral-action-btn banner-settings-toggle referral-banner-settings-btn"
                      onClick={() => setShowBannerSettings(true)}
                    >
                      Настройки
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {referralErr && <p className="error-text">{referralErr}</p>}
                <p className="muted">Загрузка кода…</p>
              </>
            )}
          </div>

          <div className="card promo-card promo-history-card">
            <h3 className="promo-card-title">
              <img
                className="promo-card-title-icon"
                src={promoIconUrl(PROMO_ICONS.calendar)}
                alt=""
                aria-hidden
              />
              История
            </h3>
            <p className="muted promo-card-desc">
              Кто активировал ваш код и какие коды активировали вы.
            </p>

            <div className="promo-history-tabs">
              <button
                type="button"
                className={`promo-history-tab${historyTab === 'invitees' ? ' active' : ''}`}
                onClick={() => {
                  setHistoryTab('invitees');
                  setHistoryVisible(8);
                }}
              >
                Активировали ваш код
                <span className="promo-history-tab-count">
                  {promoHistory ? promoHistory.invitees.length : '…'}
                </span>
              </button>
              <button
                type="button"
                className={`promo-history-tab${historyTab === 'activations' ? ' active' : ''}`}
                onClick={() => {
                  setHistoryTab('activations');
                  setHistoryVisible(8);
                }}
              >
                Вы активировали
                <span className="promo-history-tab-count">
                  {promoHistory ? promoHistory.activations.length : '…'}
                </span>
              </button>
            </div>

            {historyTab === 'invitees' ? (
              <div className="promo-history-section">
                {!promoHistory ? (
                  <p className="muted promo-history-empty">Загрузка…</p>
                ) : promoHistory.invitees.length === 0 ? (
                  <p className="muted promo-history-empty">Пока никто не активировал ваш код.</p>
                ) : (
                  <>
                    <ul className="promo-history-list">
                      {promoHistory.invitees.slice(0, historyVisible).map((item, idx) => (
                        <li
                          key={`${item.display_name}-${item.used_at ?? idx}`}
                          className="promo-history-item"
                        >
                          <span className="promo-history-name">{item.display_name}</span>
                          <span className="promo-history-meta muted">
                            {item.used_at ? formatDate(item.used_at) : '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {promoHistory.invitees.length > historyVisible ? (
                      <button
                        type="button"
                        className="promo-history-more"
                        onClick={() => setHistoryVisible((n) => n + 5)}
                      >
                        Показать ещё
                        <IconChevron size={14} />
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            ) : (
              <div className="promo-history-section">
                {!promoHistory ? (
                  <p className="muted promo-history-empty">Загрузка…</p>
                ) : promoHistory.activations.length === 0 ? (
                  <p className="muted promo-history-empty">Вы ещё не активировали коды.</p>
                ) : (
                  <>
                    <ul className="promo-history-list">
                      {promoHistory.activations.slice(0, historyVisible).map((item, idx) => (
                        <li
                          key={`${item.kind}-${item.code}-${item.used_at ?? idx}`}
                          className="promo-history-item"
                        >
                          <span className="promo-history-name">
                            <code>{item.code}</code>
                            <span className="promo-history-kind">
                              {item.kind === 'invite' ? 'инвайт' : 'промо'}
                            </span>
                          </span>
                          <span className="promo-history-meta muted">
                            {item.bonus_days != null ? `+${item.bonus_days} дн. · ` : ''}
                            {item.used_at ? formatDate(item.used_at) : '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {promoHistory.activations.length > historyVisible ? (
                      <button
                        type="button"
                        className="promo-history-more"
                        onClick={() => setHistoryVisible((n) => n + 5)}
                      >
                        Показать ещё
                        <IconChevron size={14} />
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>

          {invite ? (
            <div className="promo-stat-cards promo-stats-block">
              <div className="promo-stat-card is-invited">
                <img
                  className="promo-stat-icon"
                  src={promoIconUrl(PROMO_ICONS.userAdd)}
                  alt=""
                  aria-hidden
                />
                <div className="promo-stat-body">
                  <span className="promo-stat-label">Всего приглашено</span>
                  <strong className="promo-stat-value">
                    {invite.friends_count ?? 0}{' '}
                    <span className="promo-stat-unit">пользователей</span>
                  </strong>
                </div>
              </div>
              <div className="promo-stat-card is-active">
                <img
                  className="promo-stat-icon"
                  src={promoIconUrl(PROMO_ICONS.userFollowing)}
                  alt=""
                  aria-hidden
                />
                <div className="promo-stat-body">
                  <span className="promo-stat-label">Активных</span>
                  <strong className="promo-stat-value">
                    {invite.friends_active_count ?? 0}{' '}
                    <span className="promo-stat-unit">пользователей</span>
                  </strong>
                </div>
              </div>
              <div className="promo-stat-card is-bonus">
                <img
                  className="promo-stat-icon"
                  src={promoIconUrl(PROMO_ICONS.sparkles)}
                  alt=""
                  aria-hidden
                />
                <div className="promo-stat-body">
                  <span className="promo-stat-label">Бонусов получено</span>
                  <strong className="promo-stat-value">
                    +{invite.bonus_days_earned ?? 0}{' '}
                    <span className="promo-stat-unit">
                      {pluralDays(invite.bonus_days_earned ?? 0)} всего
                    </span>
                  </strong>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {nav === 'help' && (
        <HelpCenter
          active
          onGoToKeys={() => openProfile('vpn')}
          onOpenSupport={() => {
            requestOpenSupportCreate();
            setNav('support');
          }}
          botUrl={botUrl}
        />
      )}

      {nav === 'support' && <SupportSection />}

      {nav === 'settings' && profile && (
        <div className="settings-layout">
          <div className="settings-main">
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Аккаунт</h3>
              <div className="settings-row">
                <span className="muted">Telegram ID</span>
                <span>{profile.telegram_user_id}</span>
              </div>
              <div className="settings-row">
                <span className="muted">Имя</span>
                <span>{profile.first_name ?? '—'}</span>
              </div>
              <div className="settings-row">
                <span className="muted">Username</span>
                <span>{profile.username ? `@${profile.username}` : '—'}</span>
              </div>
              <div className="settings-row">
                <span className="muted">Язык</span>
                <span>{profile.language.toUpperCase()}</span>
              </div>

              <h4 className="settings-subheading">Вход на сайт</h4>
              <div className="settings-row">
                <span className="muted">Логин</span>
                <span className="settings-mono">{siteUsername ?? '—'}</span>
              </div>
              <label className="settings-field-label" htmlFor="site-new-password">
                Новый пароль
              </label>
              <input
                id="site-new-password"
                className="input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={8}
                autoComplete="new-password"
                placeholder="минимум 8 символов"
              />
              <div className="settings-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={sitePassBusy || newPassword.length < 8}
                  onClick={() => {
                    void (async () => {
                      setSitePassBusy(true);
                      setSitePassErr(null);
                      setSitePassMsg(null);
                      setGeneratedPassword(null);
                      try {
                        const res = await api.setPassword(newPassword);
                        if (res.success && res.data?.username) {
                          setSiteUsername(res.data.username);
                          setNewPassword('');
                          setSitePassMsg('Пароль сохранён');
                        } else {
                          setSitePassErr(res.error?.message ?? 'Не удалось сохранить пароль');
                        }
                      } catch {
                        setSitePassErr('Не удалось сохранить пароль');
                      } finally {
                        setSitePassBusy(false);
                      }
                    })();
                  }}
                >
                  Сохранить
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={sitePassBusy}
                  onClick={() => {
                    void (async () => {
                      setSitePassBusy(true);
                      setSitePassErr(null);
                      setSitePassMsg(null);
                      try {
                        const res = await api.regeneratePassword();
                        if (res.success && res.data?.username) {
                          setSiteUsername(res.data.username);
                          if (res.data.password) setGeneratedPassword(res.data.password);
                          setSitePassMsg('Новый пароль сгенерирован');
                        } else {
                          setSitePassErr(res.error?.message ?? 'Не удалось сгенерировать');
                        }
                      } catch {
                        setSitePassErr('Не удалось сгенерировать');
                      } finally {
                        setSitePassBusy(false);
                      }
                    })();
                  }}
                >
                  Сгенерировать
                </button>
              </div>
              {generatedPassword ? (
                <p className="settings-generated">
                  Новый пароль: <code>{generatedPassword}</code>
                </p>
              ) : null}
              {sitePassMsg ? <p className="success-text">{sitePassMsg}</p> : null}
              {sitePassErr ? <p className="error-text">{sitePassErr}</p> : null}
            </div>
          </div>

          <div className="settings-side">
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Оформление</h3>
              <div className="settings-row settings-row-stack">
                <span className="muted">Тема</span>
                <div className="pref-chips">
                  {(
                    [
                      ['light', 'Светлая'],
                      ['dark', 'Тёмная'],
                      ['system', 'Системная'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`server-chip ${theme === value ? 'active' : ''}`}
                      onClick={() => setTheme(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="settings-row settings-row-stack">
                <span className="muted">Расположение меню</span>
                <div className="pref-chips">
                  <button
                    type="button"
                    className={`server-chip ${navPosition === 'top' ? 'active' : ''}`}
                    onClick={() => setNavPosition('top')}
                  >
                    Сверху
                  </button>
                  <button
                    type="button"
                    className={`server-chip ${navPosition === 'bottom' ? 'active' : ''}`}
                    onClick={() => setNavPosition('bottom')}
                  >
                    Снизу
                  </button>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 style={{ marginTop: 0 }}>Поддержка</h3>
              <p className="muted settings-hint">
                Вопросы по подписке и ключам — оставьте тикет в разделе «Поддержка».
              </p>
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => {
                  requestOpenSupportCreate();
                  setNav('support');
                }}
              >
                Создать обращение
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      )}

      {bannerSettingsUi.mounted && invite && (
        <div
          className={`modal-backdrop${bannerSettingsUi.visible ? ' is-open' : ''}`}
          role="presentation"
          onMouseDown={(e) => {
            bannerBackdropDownRef.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (bannerBackdropDownRef.current && e.target === e.currentTarget) {
              setShowBannerSettings(false);
            }
            bannerBackdropDownRef.current = false;
          }}
        >
          <div
            className="modal-sheet banner-settings-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="banner-settings-title"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="banner-settings-modal-head">
              <div>
                <h3 id="banner-settings-title">Настройки баннера</h3>
                <p className="muted banner-settings-modal-sub">
                  Меняется вживую справа. После правок скопируй URL заново в OBS.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-ghost banner-settings-modal-close"
                aria-label="Закрыть"
                onClick={() => setShowBannerSettings(false)}
              >
                <IconX size={18} />
              </button>
            </div>

            <div className="banner-settings-layout">
              <div className="banner-settings-controls">
                <div className="banner-settings-tabs" role="tablist">
                  {(
                    [
                      { id: 'content' as const, label: 'Контент' },
                      { id: 'look' as const, label: 'Вид' },
                      { id: 'motion' as const, label: 'Движение' },
                    ] as const
                  ).map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={bannerSettingsTab === tab.id}
                      className={`banner-settings-tab${bannerSettingsTab === tab.id ? ' active' : ''}`}
                      onClick={() => setBannerSettingsTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="banner-opts banner-settings-panel">
                  {bannerSettingsTab === 'content' ? (
                    <>
                      <div className="banner-opts-row">
                        <span className="banner-opts-label">Макет</span>
                        <div className="pref-chips">
                          {BANNER_LAYOUT_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={`server-chip ${bannerOpts.layout === opt.value ? 'active' : ''}`}
                              onClick={() =>
                                updateBannerOpts({ layout: opt.value as BannerLayout })
                              }
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <label className="banner-opts-field banner-opts-text">
                        <span className="banner-opts-label">Текст баннера</span>
                        <textarea
                          className="input"
                          rows={5}
                          maxLength={360}
                          placeholder={
                            bannerOpts.layout === 'simple'
                              ? DEFAULT_SIMPLE_BANNER_TEXT
                              : 'Слайд 1 | Слайд 2 | Используй {code} — +{days} дней'
                          }
                          value={bannerOpts.text}
                          onChange={(e) => updateBannerOpts({ text: e.target.value })}
                        />
                        <span className="muted banner-opts-hint banner-opts-hint-fixed">
                          {bannerOpts.layout === 'simple' ? (
                            <>
                              Одна строка. Можно {'{code}'} и {'{days}'}. Пусто = текст по
                              умолчанию.
                            </>
                          ) : (
                            <>
                              Слайды через <code>|</code>. Пример:{' '}
                              <code>
                                Зависает стрим? | Стабильный VPN | Промокод {'{code}'}
                              </code>
                              . Пусто = стандартные слайды.
                            </>
                          )}
                        </span>
                      </label>
                    </>
                  ) : null}

                  {bannerSettingsTab === 'look' ? (
                    <>
                      <div className="banner-opts-row">
                        <span className="banner-opts-label">Готовые темы</span>
                        <div className="pref-chips">
                          {BANNER_THEME_PRESETS.map((preset) => (
                            <button
                              key={preset.id}
                              type="button"
                              className="server-chip"
                              onClick={() =>
                                updateBannerOpts({
                                  bg: preset.bg,
                                  fg: preset.fg,
                                  accent: preset.accent,
                                  fx: preset.fx,
                                })
                              }
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="banner-opts-colors">
                        <label className="banner-opts-field">
                          <span className="banner-opts-label">Фон</span>
                          <input
                            className="input banner-color-input"
                            type="color"
                            value={bannerOpts.bg}
                            onChange={(e) => updateBannerOpts({ bg: e.target.value })}
                          />
                        </label>
                        <label className="banner-opts-field">
                          <span className="banner-opts-label">Текст</span>
                          <input
                            className="input banner-color-input"
                            type="color"
                            value={bannerOpts.fg}
                            onChange={(e) => updateBannerOpts({ fg: e.target.value })}
                          />
                        </label>
                        <label className="banner-opts-field">
                          <span className="banner-opts-label">Акцент</span>
                          <input
                            className="input banner-color-input"
                            type="color"
                            value={bannerOpts.accent}
                            onChange={(e) => updateBannerOpts({ accent: e.target.value })}
                          />
                        </label>
                        <label className="banner-opts-field banner-opts-check">
                          <span className="banner-opts-label">Эффекты</span>
                          <input
                            type="checkbox"
                            checked={bannerOpts.fx}
                            onChange={(e) => updateBannerOpts({ fx: e.target.checked })}
                          />
                        </label>
                      </div>
                    </>
                  ) : null}

                  {bannerSettingsTab === 'motion' ? (
                    <>
                      <div className="banner-opts-row">
                        <span className="banner-opts-label">Режим</span>
                        <div className="pref-chips">
                          {(
                            [
                              { value: 'static' as BannerMode, label: 'Статичный' },
                              { value: 'enter' as BannerMode, label: 'Появление' },
                            ] as const
                          ).map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={`server-chip ${bannerOpts.mode === opt.value ? 'active' : ''}`}
                              onClick={() => updateBannerOpts({ mode: opt.value })}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {bannerOpts.mode === 'enter' ? (
                        <>
                          <div className="banner-opts-row">
                            <span className="banner-opts-label">Откуда появляется</span>
                            <div className="pref-chips">
                              {BANNER_FROM_OPTIONS.map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  className={`server-chip ${bannerOpts.from === opt.value ? 'active' : ''}`}
                                  onClick={() =>
                                    updateBannerOpts({ from: opt.value as BannerFrom })
                                  }
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="banner-opts-timers">
                            <label className="banner-opts-field">
                              <span className="banner-opts-label">На экране, сек</span>
                              <input
                                className="input"
                                type="number"
                                min={5}
                                max={300}
                                value={bannerOpts.holdSec}
                                onChange={(e) =>
                                  updateBannerOpts({ holdSec: Number(e.target.value) || 5 })
                                }
                              />
                            </label>
                            <label className="banner-opts-field">
                              <span className="banner-opts-label">Пауза до след., сек</span>
                              <input
                                className="input"
                                type="number"
                                min={3}
                                max={600}
                                value={bannerOpts.everySec}
                                onChange={(e) =>
                                  updateBannerOpts({ everySec: Number(e.target.value) || 3 })
                                }
                              />
                            </label>
                          </div>
                          <p className="muted banner-opts-hint">
                            В превью цикл ускорен, в OBS — ваши секунды как задано.
                          </p>
                        </>
                      ) : (
                        <p className="muted banner-opts-hint">
                          Баннер всегда на экране. Карусель крутит слайды по кругу.
                        </p>
                      )}
                    </>
                  ) : null}
                </div>
              </div>

              <div className="banner-settings-preview-col">
                <span className="banner-opts-label">Превью</span>
                <BannerLivePreview
                  code={invite.code}
                  bonusDays={invite.bonus_days}
                  opts={bannerOpts}
                  framed
                  pace="preview"
                />
                <p className="muted banner-settings-preview-note">
                  Как в OBS Browser Source: 600×160, прозрачный фон вокруг.
                </p>
              </div>
            </div>

            <div className="banner-settings-footer">
              <button type="button" className="btn btn-ghost" onClick={resetBannerDesign}>
                Сброс вида
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowBannerSettings(false)}
              >
                Готово
              </button>
            </div>
          </div>
        </div>
      )}

      {referralEditUi.mounted && invite && (
        <div
          className={`modal-backdrop${referralEditUi.visible ? ' is-open' : ''}`}
          role="presentation"
          onMouseDown={(e) => {
            referralBackdropDownRef.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (
              !referralBusy &&
              referralBackdropDownRef.current &&
              e.target === e.currentTarget
            ) {
              setShowReferralEdit(false);
              setNewReferralCode(invite.code);
              setReferralErr(null);
            }
            referralBackdropDownRef.current = false;
          }}
        >
          <div
            className="modal-sheet referral-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="referral-edit-title"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="banner-settings-modal-head">
              <div>
                <h3 id="referral-edit-title">Изменить личный код</h3>
                <p className="muted banner-settings-modal-sub">
                  Смена доступна раз в 30 дней. Текущий код: <code>{invite.code}</code>
                </p>
              </div>
              <button
                type="button"
                className="btn btn-ghost banner-settings-modal-close"
                aria-label="Закрыть"
                disabled={referralBusy}
                onClick={() => {
                  setShowReferralEdit(false);
                  setNewReferralCode(invite.code);
                  setReferralErr(null);
                }}
              >
                <IconX size={18} />
              </button>
            </div>

            <form className="referral-edit-form" onSubmit={updateReferralCode}>
              <p className="muted referral-edit-hint">
                Новый код: 4–16 символов, A–Z и 0–9. Нельзя промокоды, служебные слова и
                оскорбления.
              </p>
              <input
                className="input"
                placeholder="НОВЫЙКОД"
                value={newReferralCode}
                onChange={(e) =>
                  setNewReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                }
                minLength={4}
                maxLength={16}
                required
                autoFocus
                disabled={referralBusy}
              />
              {referralErr ? <p className="error-text">{referralErr}</p> : null}
              <div className="modal-actions referral-edit-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={referralBusy}
                  onClick={() => {
                    setShowReferralEdit(false);
                    setNewReferralCode(invite.code);
                    setReferralErr(null);
                  }}
                >
                  Отмена
                </button>
                <button type="submit" className="btn btn-primary" disabled={referralBusy}>
                  {referralBusy ? 'Сохранение…' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {downloadPickerUi.mounted && (
        <div
          className={`modal-backdrop${downloadPickerUi.visible ? ' is-open' : ''}`}
          role="presentation"
          onMouseDown={(e) => {
            downloadBackdropDownRef.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (
              !downloadBusy &&
              downloadBackdropDownRef.current &&
              e.target === e.currentTarget
            ) {
              setDownloadPickerOpen(false);
            }
            downloadBackdropDownRef.current = false;
          }}
        >
          <div
            className="modal-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="download-picker-title"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="download-picker-title" style={{ marginTop: 0 }}>
              Скачать конфиги
            </h3>
            <p className="muted" style={{ fontSize: '0.875rem', marginTop: 0 }}>
              Выберите ключи, которые нужно скачать.
            </p>
            <div className="download-picker-list">
              {wgKeys.map((k) => {
                const id = `wg:${k.id}`;
                return (
                  <label key={id} className="download-picker-item">
                    <input
                      type="checkbox"
                      checked={downloadSelected.includes(id)}
                      disabled={downloadBusy}
                      onChange={() => toggleDownloadItem(id)}
                    />
                    <span>
                      <strong>{k.server_country.toUpperCase()}</strong>
                      <span className="badge badge-muted" style={{ marginLeft: 8 }}>
                        WG
                      </span>
                    </span>
                  </label>
                );
              })}
              {ovpnKeys.map((k) => {
                const id = `ovpn:${k.id}`;
                return (
                  <label key={id} className="download-picker-item">
                    <input
                      type="checkbox"
                      checked={downloadSelected.includes(id)}
                      disabled={downloadBusy}
                      onChange={() => toggleDownloadItem(id)}
                    />
                    <span>
                      <strong>{k.server_code.toUpperCase()}</strong>
                      <span className="badge badge-muted" style={{ marginLeft: 8 }}>
                        OVPN
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={downloadBusy}
                onClick={() =>
                  setDownloadSelected([
                    ...wgKeys.map((k) => `wg:${k.id}`),
                    ...ovpnKeys.map((k) => `ovpn:${k.id}`),
                  ])
                }
              >
                Все
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={downloadBusy}
                onClick={() => setDownloadPickerOpen(false)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={downloadBusy || downloadSelected.length === 0}
                onClick={() => void downloadSelectedConfigs()}
              >
                {downloadBusy
                  ? 'Скачивание…'
                  : `Скачать (${downloadSelected.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateKeyModal
        open={createKeyOpen}
        onClose={() => {
          if (!wgBusy && !ovpnBusy) setCreateKeyOpen(false);
        }}
        protocol={createKeyProtocol}
        onProtocolChange={(p) => {
          setCreateKeyProtocol(p);
          const servers = p === 'wg' ? wgServers : ovpnServers;
          const preferred =
            p === 'wg'
              ? selectedWgServer || servers[0] || ''
              : selectedOvpnServer || servers[0] || '';
          setCreateKeyRegion(preferred);
        }}
        region={createKeyRegion}
        onRegionChange={setCreateKeyRegion}
        regions={vpnRegions}
        wgServers={wgServers}
        ovpnServers={ovpnServers}
        subscriptionTier={sub?.subscription_tier ?? 'default'}
        subscriptionStatus={subStatus}
        subscriptionUntilLabel={
          sub?.subscription_until && subStatus !== 'lifetime'
            ? formatDateLong(sub.subscription_until)
            : subStatus === 'lifetime'
              ? 'бессрочно'
              : subStatus === 'keys_only'
                ? 'нужно продлить'
                : undefined
        }
        maxPerRegion={maxPerRegion}
        maxOvpnKeys={maxOvpnKeys}
        wgKeysOnRegion={createKeyWgOnRegion}
        ovpnKeysCount={ovpnTabCount}
        busy={wgBusy || ovpnBusy}
        error={createKeyOpen ? keyErr : null}
        onCreate={() => {
          if (createKeyProtocol === 'wg') void createWgKey(createKeyRegion);
          else void createOvpnKey(createKeyRegion);
        }}
      />
    </CabinetLayout>
  );
}

function referralErrorLabel(code: string, fallback: string): string {
  switch (code) {
    case 'referral_format':
      return 'Неверный формат. Используйте 4–16 символов: A–Z и 0–9.';
    case 'referral_blacklist':
      return 'Этот код нельзя использовать. Выберите другой.';
    case 'referral_reserved':
      return 'Этот код зарезервирован системой или совпадает с промокодом.';
    case 'referral_taken':
      return 'Этот код уже занят другим пользователем.';
    case 'referral_cooldown':
      return fallback || 'Сменить код можно раз в 30 дней.';
    case 'referral_unavailable':
      return 'Реферальные коды пока недоступны. Обновите бота на сервере (миграция 011).';
    default:
      return fallback;
  }
}
